import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  CreditCard,
  Receipt,
  Bell,
  TrendingUp,
  Clock,
  FileText,
  Activity,
  DollarSign,
  UserPlus,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import {
  systemAnalyticsService,
  SecretaryDashboardStats,
} from "../../services/system-analytics";

// Reusable Stats Card Component
interface StatsCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color: "blue" | "green" | "yellow" | "red" | "purple" | "indigo";
  link?: string;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  trend,
  color,
  link,
}) => {
  const colorClasses = {
    blue: "from-blue-300 to-blue-400",
    green: "from-emerald-300 to-emerald-400",
    yellow: "from-amber-300 to-yellow-400",
    red: "from-red-300 to-pink-400",
    purple: "from-purple-300 to-purple-400",
    indigo: "from-indigo-300 to-indigo-400",
  };

  const CardContent = (
    <div
      className={`bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 border border-slate-200/60 p-6 ${
        link ? "cursor-pointer" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-3">
            <div
              className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-md`}
            >
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-600">{title}</h3>
              <p className="text-2xl font-bold text-slate-900">{value}</p>
            </div>
          </div>

          {subtitle && (
            <p className="text-sm text-slate-600 mb-2 font-medium">
              {subtitle}
            </p>
          )}

          {trend && (
            <div className="flex items-center gap-1">
              <TrendingUp
                className={`w-4 h-4 ${
                  trend.isPositive ? "text-emerald-500" : "text-red-500"
                }`}
              />
              <span
                className={`text-sm font-bold ${
                  trend.isPositive ? "text-emerald-600" : "text-red-600"
                }`}
              >
                {trend.isPositive ? "+" : ""}
                {trend.value}%
              </span>
              <span className="text-sm text-slate-500 font-medium">
                vs last month
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return link ? (
    <Link to={link} className="block">
      {CardContent}
    </Link>
  ) : (
    CardContent
  );
};

const SecretaryDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<SecretaryDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response =
          await systemAnalyticsService.getSecretaryDashboardStats();

        // The service already extracts the data from the API response
        // So response is directly the dashboard stats object
        const dashboardData = response as any;

        if (dashboardData && dashboardData.studentsRegistered) {
          setStats(dashboardData);
          setRecentActivity(dashboardData.recentActivity || []);
        } else {
          console.error("Invalid response structure:", response);
          setError("Invalid response from server");
        }
      } catch (err: any) {
        console.error("Error fetching secretary dashboard stats:", err);
        setError(
          err.response?.data?.message || "Failed to load dashboard data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Show loading state
  if (loading) {
    return <LoadingSpinner />;
  }

  // Show error state
  if (error || !stats) {
    return (
      <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>
            <p className="text-red-600 font-semibold text-lg">
              {error || "Failed to load dashboard data"}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Secretary Dashboard
              </h1>
              <p className="text-slate-600 mt-1 font-medium">
                Welcome back, {user?.email?.split("@")[0]}! Manage student
                registrations and fee collections.
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                {currentDate}
              </p>
            </div>
          </div>
        </div>

        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Students Registered"
            value={stats.studentsRegistered.total}
            subtitle={`${stats.studentsRegistered.thisMonth} this month`}
            icon={Users}
            trend={{
              value: stats.studentsRegistered.growth,
              isPositive: true,
            }}
            color="blue"
            link="/secretary/students"
          />

          <StatsCard
            title="Payments Processed"
            value={stats.paymentsProcessed.thisMonth}
            subtitle={`KSh ${stats.paymentsProcessed.amount.toLocaleString()} collected`}
            icon={CreditCard}
            trend={{
              value: stats.paymentsProcessed.growth,
              isPositive: true,
            }}
            color="green"
            link="/secretary/payments"
          />

          <StatsCard
            title="Receipts Generated"
            value={stats.receiptsGenerated.thisMonth}
            subtitle={`${stats.receiptsGenerated.total} total receipts`}
            icon={Receipt}
            trend={{
              value: stats.receiptsGenerated.growth,
              isPositive: true,
            }}
            color="purple"
            link="/secretary/receipts"
          />

          <StatsCard
            title="Pending Tasks"
            value={
              stats.pendingTasks.newStudents +
              stats.pendingTasks.pendingPayments +
              stats.pendingTasks.expiredFees
            }
            subtitle="Requires attention"
            icon={AlertCircle}
            color="yellow"
          />
        </div>

        {/* Action Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Quick Actions */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                <Activity className="w-5 h-5 text-white" />
              </div>
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <Link
                to="/secretary/students"
                className="p-4 bg-gradient-to-br from-blue-50 to-blue-100/80 rounded-xl hover:from-blue-100 hover:to-blue-200/80 transition-all duration-300 group border border-blue-200/60 shadow-md hover:shadow-xl hover:scale-105"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">
                  Register Student
                </h3>
                <p className="text-sm text-slate-600 font-medium">
                  Add new student records
                </p>
              </Link>

              <Link
                to="/secretary/payments"
                className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100/80 rounded-xl hover:from-emerald-100 hover:to-emerald-200/80 transition-all duration-300 group border border-emerald-200/60 shadow-md hover:shadow-xl hover:scale-105"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">
                  Record Payment
                </h3>
                <p className="text-sm text-slate-600 font-medium">
                  Process fee payments
                </p>
              </Link>

              <Link
                to="/secretary/receipts"
                className="p-4 bg-gradient-to-br from-purple-50 to-purple-100/80 rounded-xl hover:from-purple-100 hover:to-purple-200/80 transition-all duration-300 group border border-purple-200/60 shadow-md hover:shadow-xl hover:scale-105"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">
                  Generate Receipt
                </h3>
                <p className="text-sm text-slate-600 font-medium">
                  Create payment receipts
                </p>
              </Link>

              <Link
                to="/secretary/announcements"
                className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100/80 rounded-xl hover:from-indigo-100 hover:to-indigo-200/80 transition-all duration-300 group border border-indigo-200/60 shadow-md hover:shadow-xl hover:scale-105"
              >
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-300 to-indigo-400 flex items-center justify-center mb-3 shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Bell className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 mb-1">Send Notice</h3>
                <p className="text-sm text-slate-600 font-medium">
                  Communicate with users
                </p>
              </Link>
            </div>
          </div>

          {/* Pending Tasks Detail */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60">
            <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-md">
                <Clock className="w-5 h-5 text-white" />
              </div>
              Pending Tasks
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gradient-to-br from-amber-50 to-yellow-50/80 rounded-xl border border-yellow-200/60 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-md">
                    <UserPlus className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">
                      New Student Applications
                    </h3>
                    <p className="text-sm text-slate-600 font-medium">
                      Pending registration
                    </p>
                  </div>
                </div>
                <span className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                  {stats.pendingTasks.newStudents}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-br from-blue-50 to-blue-100/80 rounded-xl border border-blue-200/60 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">
                      Pending Payments
                    </h3>
                    <p className="text-sm text-slate-600 font-medium">
                      Awaiting processing
                    </p>
                  </div>
                </div>
                <span className="bg-gradient-to-r from-blue-400 to-blue-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                  {stats.pendingTasks.pendingPayments}
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-gradient-to-br from-red-50 to-pink-50/80 rounded-xl border border-red-200/60 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center shadow-md">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Overdue Fees</h3>
                    <p className="text-sm text-slate-600 font-medium">
                      Require follow-up
                    </p>
                  </div>
                </div>
                <span className="bg-gradient-to-r from-red-400 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-bold shadow-md">
                  {stats.pendingTasks.expiredFees}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 shadow-lg border border-slate-200/60">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-md">
              <Activity className="w-5 h-5 text-white" />
            </div>
            Recent Activity
          </h2>
          {recentActivity.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-3 shadow-md">
                <Activity className="w-8 h-8 text-slate-500" />
              </div>
              <p className="text-slate-500 font-medium">No recent activity</p>
            </div>
          ) : (
            <div className="space-y-4">
              {recentActivity.map((activity, index) => {
                const IconComponent =
                  activity.icon === "UserPlus"
                    ? UserPlus
                    : activity.icon === "CreditCard"
                    ? CreditCard
                    : activity.icon === "Receipt"
                    ? Receipt
                    : CheckCircle;
                const iconGradient =
                  activity.type === "payment"
                    ? "from-emerald-300 to-emerald-400"
                    : activity.type === "registration"
                    ? "from-blue-300 to-blue-400"
                    : "from-purple-300 to-purple-400";

                const timeAgo = (timestamp: string) => {
                  const now = new Date();
                  const activityTime = new Date(timestamp);
                  const diffMs = now.getTime() - activityTime.getTime();
                  const diffMins = Math.floor(diffMs / 60000);
                  const diffHours = Math.floor(diffMins / 60);
                  const diffDays = Math.floor(diffHours / 24);

                  if (diffDays > 0)
                    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
                  if (diffHours > 0)
                    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
                  if (diffMins > 0)
                    return `${diffMins} minute${diffMins > 1 ? "s" : ""} ago`;
                  return "Just now";
                };

                return (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-4 bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-xl border border-slate-200/60 shadow-sm hover:shadow-md transition-all duration-300"
                  >
                    <div
                      className={`w-10 h-10 rounded-lg bg-gradient-to-br ${iconGradient} flex items-center justify-center shadow-md flex-shrink-0`}
                    >
                      <IconComponent className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900">
                        {activity.title}
                      </p>
                      <p className="text-sm text-slate-600 font-medium">
                        {activity.description}
                      </p>
                    </div>
                    <span className="text-sm text-slate-500 font-semibold whitespace-nowrap">
                      {timeAgo(activity.timestamp)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SecretaryDashboard;
